
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Drawer, List, ListItem, ListItemIcon, ListItemText,
    AppBar, Toolbar, Typography, Avatar, IconButton,
    Chip, useMediaQuery, useTheme, Menu, MenuItem,
    Divider, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Alert, CircularProgress, InputAdornment,
} from "@mui/material";
import InventoryIcon      from "@mui/icons-material/Inventory";
import EmailIcon          from "@mui/icons-material/Email";
import WorkOutlineIcon    from "@mui/icons-material/WorkOutline";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import DashboardIcon      from "@mui/icons-material/Dashboard";
import LogoutIcon         from "@mui/icons-material/Logout";
import MenuIcon           from "@mui/icons-material/Menu";
import PersonIcon         from "@mui/icons-material/Person";
import LockResetIcon      from "@mui/icons-material/LockReset";
import LockOutlinedIcon   from "@mui/icons-material/LockOutlined";
import Visibility         from "@mui/icons-material/Visibility";
import VisibilityOff      from "@mui/icons-material/VisibilityOff";
import BadgeIcon          from "@mui/icons-material/Badge";
import EmailOutlinedIcon  from "@mui/icons-material/EmailOutlined";

import AdminDashboardHome from "./AdminDashboardHome";
import AdminProducts      from "./AdminProducts";
import AdminContacts      from "./AdminContacts";
import AdminCareers       from "./AdminCareers";
import AdminInquiries     from "./AdminInquiries";
import axiosInstance      from "./Axiosinstance";

const DRAWER_WIDTH = 260;

const menuItems = [
    { id: "dashboard", label: "Dashboard",           icon: <DashboardIcon /> },
    { id: "products",  label: "Products",            icon: <InventoryIcon /> },
    { id: "contacts",  label: "Contact Enquiries",   icon: <EmailIcon /> },
    { id: "inquiries", label: "Product Enquiries",   icon: <QuestionAnswerIcon /> },
    { id: "careers",   label: "Career Applications", icon: <WorkOutlineIcon /> },
];   

// ── Change Password Dialog ────────────────────────────────────────────────────
const ChangePasswordDialog = ({ open, onClose }) => {
    const [form, setForm]       = useState({ current: "", newPass: "", confirm: "" });
    const [show, setShow]       = useState({ current: false, newPass: false, confirm: false });
    const [error, setError]     = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setForm({ current: "", newPass: "", confirm: "" });
        setError(""); setSuccess(false);
        onClose();
    };

    const handleSubmit = async () => {
        if (!form.current)               { setError("Current password is required"); return; }
        if (form.newPass.length < 8)     { setError("New password must be at least 8 characters"); return; }
        if (form.newPass !== form.confirm){ setError("Passwords do not match"); return; }

        setLoading(true); setError("");
        try {
            const userId = localStorage.getItem("userId");
            await axiosInstance.post("/api/auth/change-password", {
                userId,
                currentPassword: form.current,
                newPassword: form.newPass,
            });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to change password");
        } finally {
            setLoading(false);
        }
    };

    const fields = [
        { key: "current", label: "Current Password",  placeholder: "Enter current password" },
        { key: "newPass", label: "New Password",       placeholder: "Min. 8 characters" },
        { key: "confirm", label: "Confirm Password",   placeholder: "Re-enter new password" },
    ];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LockResetIcon color="primary" /> Change Password
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2.5 }}>
                {success ? (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                        <Box sx={{
                            width: 56, height: 56, borderRadius: "50%",
                            background: "#e8f5e9", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            mx: "auto", mb: 1.5, fontSize: 28,
                        }}>✅</Box>
                        <Typography fontWeight={600}>Password changed successfully!</Typography>
                    </Box>
                ) : (
                    <Box>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
                                {error}
                            </Alert>
                        )}
                        {fields.map(({ key, label, placeholder }) => (
                            <Box key={key} sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{label}</Typography>
                                <TextField
                                    fullWidth size="small"
                                    type={show[key] ? "text" : "password"}
                                    placeholder={placeholder}
                                    value={form[key]}
                                    onChange={(e) => { setForm(p => ({ ...p, [key]: e.target.value })); setError(""); }}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" tabIndex={-1}
                                                    onClick={() => setShow(p => ({ ...p, [key]: !p[key] }))}>
                                                    {show[key]
                                                        ? <VisibilityOff sx={{ fontSize: 18 }} />
                                                        : <Visibility sx={{ fontSize: 18 }} />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                {success ? (
                    <Button fullWidth variant="contained" onClick={handleClose}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
                        Done
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} disabled={loading}
                            sx={{ borderRadius: 2, textTransform: "none", color: "text.secondary" }}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={loading}
                            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600,
                                background: "linear-gradient(135deg, #1976d2, #1565c0)" }}>
                            {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Update Password"}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

// ── My Profile Dialog ─────────────────────────────────────────────────────────
const ProfileDialog = ({ open, onClose, onChangePassword }) => {
    const userName  = localStorage.getItem("userName")  || "Admin";
    const userEmail = localStorage.getItem("userEmail") || "";
    const userRole  = localStorage.getItem("userRole")  || "ADMIN";

    const getInitials = (name) =>
        name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon color="primary" /> My Profile
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 3 }}>
                {/* Avatar */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Avatar sx={{
                        width: 72, height: 72, mx: "auto", mb: 1.5,
                        fontSize: "22px", fontWeight: 700,
                        background: "linear-gradient(135deg, #1976d2, #1565c0)",
                    }}>
                        {getInitials(userName)}
                    </Avatar>
                    <Chip label={userRole} size="small" color="primary" variant="outlined"
                        sx={{ fontWeight: 700 }} />
                </Box>

                {/* Details */}
                {[
                    { icon: <BadgeIcon />,        label: "Full Name", value: userName },
                    { icon: <EmailOutlinedIcon />, label: "Email",     value: userEmail },
                    { icon: <PersonIcon />,        label: "Role",      value: userRole },
                ].map(({ icon, label, value }) => (
                    <Box key={label} sx={{
                        display: "flex", alignItems: "center", gap: 2,
                        p: 1.5, borderRadius: 2, mb: 1.5,
                        background: "rgba(25,118,210,0.04)",
                        border: "1px solid rgba(25,118,210,0.08)",
                    }}>
                        <Box sx={{ color: "primary.main" }}>{icon}</Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                {label}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>{value}</Typography>
                        </Box>
                    </Box>
                ))}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: "column", gap: 1 }}>
                <Button fullWidth variant="outlined" startIcon={<LockResetIcon />}
                    onClick={() => { onClose(); onChangePassword(); }}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
                    Change Password
                </Button>
                <Button fullWidth variant="text" onClick={onClose}
                    sx={{ borderRadius: 2, textTransform: "none", color: "text.secondary" }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Main AdminDashboard ───────────────────────────────────────────────────────
const AdminDashboard = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [activeTab,        setActiveTab]        = useState("dashboard");
    const [drawerOpen,       setDrawerOpen]       = useState(false);
    const [anchorEl,         setAnchorEl]         = useState(null);   // profile menu
    const [profileOpen,      setProfileOpen]      = useState(false);
    const [changePassOpen,   setChangePassOpen]   = useState(false);

    const userName  = localStorage.getItem("userName")  || "Admin";
    const userRole  = localStorage.getItem("userRole")  || "ADMIN";
    const userEmail = localStorage.getItem("userEmail") || "";

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) { navigate("/login", { replace: true }); return; }

        window.history.pushState(null, "", window.location.href);
        const handlePopState = () => window.history.pushState(null, "", window.location.href);
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [navigate]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        window.history.pushState({ tab }, "", window.location.href);
        if (isMobile) setDrawerOpen(false);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate("/", { replace: true });
    };

    const getInitials = (name) =>
        name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

    const renderContent = () => {
        switch (activeTab) {
            case "dashboard": return <AdminDashboardHome setActiveTab={handleTabChange} />;
            case "products":  return <AdminProducts />;
            case "contacts":  return <AdminContacts />;
            case "inquiries": return <AdminInquiries />;
            case "careers":   return <AdminCareers />;
            default:          return <AdminDashboardHome setActiveTab={handleTabChange} />;
        }
    };

    const drawerContent = (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Sidebar Header — NO logout here anymore */}
            <Box sx={{ px: 3, py: 3, background: "linear-gradient(135deg, #1976d2, #1565c0)" }}>
                <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, fontSize: "16px" }}>
                    LIDER ADMIN
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Management Panel
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2.5 }}>
                    <Avatar sx={{ width: 38, height: 38, fontSize: "13px", fontWeight: 700, background: "rgba(255,255,255,0.25)" }}>
                        {getInitials(userName)}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, lineHeight: 1.2 }}>
                            {userName}
                        </Typography>
                        <Chip label={userRole} size="small" sx={{
                            height: 18, fontSize: "10px", fontWeight: 700,
                            background: "rgba(255,255,255,0.2)", color: "#fff", mt: 0.25,
                        }} />
                    </Box>
                </Box>
            </Box>

            {/* Nav Items */}
            <List sx={{ flexGrow: 1, px: 1.5, py: 2 }}>
                {menuItems.map((item) => {
                    const isActive = activeTab === item.id;
                    return (
                        <ListItem button key={item.id} onClick={() => handleTabChange(item.id)} sx={{
                            borderRadius: "10px", mb: 0.5, py: 1.25,
                            background: isActive
                                ? "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(21,101,192,0.08))"
                                : "transparent",
                            border: isActive ? "1px solid rgba(25,118,210,0.2)" : "1px solid transparent",
                            "&:hover": { background: "rgba(25,118,210,0.06)", border: "1px solid rgba(25,118,210,0.1)" },
                        }}>
                            <ListItemIcon sx={{ minWidth: 38, color: isActive ? "primary.main" : "text.secondary" }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText primary={item.label} primaryTypographyProps={{
                                fontSize: "14px", fontWeight: isActive ? 700 : 500,
                                color: isActive ? "primary.main" : "text.primary",
                            }} />
                            {isActive && <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "primary.main" }} />}
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", background: "#f5f7fa" }}>
            {!isMobile && (
                <Drawer variant="permanent" sx={{
                    width: DRAWER_WIDTH, flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: DRAWER_WIDTH, boxSizing: "border-box",
                        border: "none", boxShadow: "2px 0 12px rgba(0,0,0,0.06)",
                    },
                }}>
                    {drawerContent}
                </Drawer>
            )}

            {isMobile && (
                <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
                    sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH } }}>
                    {drawerContent}
                </Drawer>
            )}

            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <AppBar position="static" elevation={0} sx={{
                    background: "#fff", borderBottom: "1px solid rgba(0,0,0,0.08)",
                }}>
                    <Toolbar sx={{ justifyContent: "space-between", minHeight: "64px !important" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {isMobile && (
                                <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "primary.main" }}>
                                    <MenuIcon />
                                </IconButton>
                            )}
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", fontSize: "18px" }}>
                                    {menuItems.find((m) => m.id === activeTab)?.label || "Dashboard"}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    Lider Technologies Admin Panel
                                </Typography>
                            </Box>
                        </Box>

                        {/* ── Profile Avatar → opens menu ── */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                                    {userName}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {userEmail}
                                </Typography>
                            </Box>
                            <Avatar
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                                sx={{
                                    width: 36, height: 36, fontSize: "13px", fontWeight: 700,
                                    background: "linear-gradient(135deg, #1976d2, #1565c0)",
                                    cursor: "pointer",
                                    "&:hover": { opacity: 0.9, transform: "scale(1.05)", transition: "all 0.2s" },
                                }}
                            >
                                {getInitials(userName)}
                            </Avatar>
                        </Box>

                        {/* ── Profile Dropdown Menu ── */}
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                            transformOrigin={{ horizontal: "right", vertical: "top" }}
                            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                            PaperProps={{
                                sx: {
                                    mt: 1, borderRadius: 2, minWidth: 200,
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                    border: "1px solid rgba(0,0,0,0.06)",
                                },
                            }}
                        >
                            {/* User info header in menu */}
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="body2" fontWeight={700}>{userName}</Typography>
                                <Typography variant="caption" color="text.secondary">{userEmail}</Typography>
                            </Box>

                            <Divider />

                            <MenuItem onClick={() => { setAnchorEl(null); setProfileOpen(true); }}
                                sx={{ py: 1.25, gap: 1.5 }}>
                                <PersonIcon sx={{ fontSize: 18, color: "primary.main" }} />
                                <Typography variant="body2" fontWeight={500}>My Profile</Typography>
                            </MenuItem>

                            <MenuItem onClick={() => { setAnchorEl(null); setChangePassOpen(true); }}
                                sx={{ py: 1.25, gap: 1.5 }}>
                                <LockResetIcon sx={{ fontSize: 18, color: "primary.main" }} />
                                <Typography variant="body2" fontWeight={500}>Change Password</Typography>
                            </MenuItem>

                            <Divider />

                            <MenuItem onClick={handleLogout} sx={{ py: 1.25, gap: 1.5 }}>
                                <LogoutIcon sx={{ fontSize: 18, color: "error.main" }} />
                                <Typography variant="body2" fontWeight={500} color="error.main">Logout</Typography>
                            </MenuItem>
                        </Menu>
                    </Toolbar>
                </AppBar>

                <Box sx={{ flexGrow: 1, overflow: "auto", p: { xs: 2, md: 3 } }}>
                    {renderContent()}
                </Box>
            </Box>

            {/* ── Dialogs ── */}
            <ProfileDialog
                open={profileOpen}
                onClose={() => setProfileOpen(false)}
                onChangePassword={() => setChangePassOpen(true)}
            />
            <ChangePasswordDialog
                open={changePassOpen}
                onClose={() => setChangePassOpen(false)}
            />
        </Box>
    );
};

export default AdminDashboard;
